import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  CreditInvoiceListItemDTO,
  CreditInvoicesListQuery,
} from "../api/client";
import { useCreditInvoicesQuery } from "../api/queries";
import { BulkVoidCreditInvoicesDialog } from "../ui/BulkVoidCreditInvoicesDialog";
import { CreditInvoicesNameFilter } from "../ui/CreditInvoicesNameFilter";
import { CreditInvoicesTable } from "../ui/CreditInvoicesTable";
import { VoidCreditInvoiceDialog } from "../ui/VoidCreditInvoiceDialog";

const EMPTY_CREDIT_INVOICES: CreditInvoiceListItemDTO[] = [];

export function CreditInvoicesPage() {
  const nav = useNavigate({ from: "/app/billing/credit-invoices" });
  const search = useSearch({
    from: "/app/billing/credit-invoices",
  });

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "issuedAt",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "issuedAt", dir: "desc" }];
  }, [search.sort]);

  const outstandingOnly = search.outstanding === "1";

  const nameSearch =
    typeof search.name === "string" && search.name.trim() !== ""
      ? search.name.trim()
      : undefined;

  const refTypeFilter =
    typeof search.refType === "string" && search.refType !== "all"
      ? search.refType
      : undefined;

  const refTypeForApi: CreditInvoicesListQuery["refType"] =
    refTypeFilter === "room_booking"
      ? "room_booking"
      : refTypeFilter === "package_purchase"
        ? "package_purchase"
        : refTypeFilter === "private_lesson_package"
          ? "private_lesson_package"
          : undefined;

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      refType: refTypeForApi,
      outstandingOnly,
      name: nameSearch,
      sort: sortArray,
    }),
    [
      search.limit,
      search.offset,
      outstandingOnly,
      sortArray,
      refTypeForApi,
      nameSearch,
    ],
  );

  const { data, isLoading } = useCreditInvoicesQuery(query);
  const items = data?.data ?? EMPTY_CREDIT_INVOICES;
  const total = data?.meta?.total ?? 0;

  const updateSearch = (updates: Record<string, unknown>) => {
    nav({ search: { ...search, ...updates } });
  };

  const sortBy = sortArray[0]?.field ?? "issuedAt";
  const sortOrder = sortArray[0]?.dir ?? "desc";

  const canVoid = useActionPermission("billing:void");
  const [voidInvoiceId, setVoidInvoiceId] = useState<string | null>(null);
  const [bulkVoidOpen, setBulkVoidOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const listKey = `${query.offset}:${items.map((row) => row.id).join(",")}`;
  useEffect(() => {
    setSelectedIds([]);
  }, [listKey]);

  const selectableIds = items
    .filter((row) => row.status !== "cancelled")
    .map((row) => row.id);
  const allPageSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl">ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ຈອງຫ້ອງ, ຂາຍແພັກເກດຄລາດສ໌, ແພັກເກດຮຽນສ່ວນຕົວ — ຮັບຊຳລະຕໍ່ແລະກວດສອບທີ່ນີ້
          </p>
        </div>

        <div className="mb-4 flex flex-wrap items-end gap-4">
          <div className="flex w-full min-w-[200px] max-w-xs flex-col gap-1.5 sm:w-auto">
            <Label htmlFor="credit-name-search" className="font-normal">
              ຊື່
            </Label>
            <CreditInvoicesNameFilter />
          </div>
          <div className="flex w-full min-w-[200px] max-w-xs flex-col gap-1.5 sm:w-auto">
            <Label htmlFor="credit-ref-type" className="font-normal">
              ປະເພດອ້າງອີງ
            </Label>
            <Select
              value={refTypeFilter ?? "all"}
              onValueChange={(v) =>
                updateSearch({
                  refType: v === "all" ? undefined : v,
                  offset: 0,
                })
              }
            >
              <SelectTrigger id="credit-ref-type" className="h-9 w-full">
                <SelectValue placeholder="ເລືອກປະເພດ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ທັງໝົດ</SelectItem>
                <SelectItem value="room_booking">ຈອງຫ້ອງ</SelectItem>
                <SelectItem value="package_purchase">ແພັກເກດຄລາດສ໌</SelectItem>
                <SelectItem value="private_lesson_package">
                  ແພັກເກດຮຽນສ່ວນຕົວ
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="outstanding-only"
              checked={outstandingOnly}
              onCheckedChange={(v) =>
                updateSearch({
                  outstanding: v === true ? "1" : undefined,
                  offset: 0,
                })
              }
            />
            <Label htmlFor="outstanding-only" className="font-normal">
              ສະແດງເຉພາະທີ່ຍັງຄ້າງເງິນ
            </Label>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              updateSearch({
                refType: undefined,
                outstanding: undefined,
                name: undefined,
                offset: 0,
              })
            }
          >
            ລ້າງຕົວກອງ
          </Button>
        </div>

        {canVoid ? (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
            <p className="text-sm">
              {selectedIds.length > 0
                ? `ເລືອກ ${selectedIds.length} ໃບ`
                : "ເລືອກໃບເພື່ອຍົກເລີກ"}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={selectableIds.length === 0}
                onClick={() =>
                  setSelectedIds(allPageSelected ? [] : selectableIds)
                }
              >
                {allPageSelected ? "ຍົກເລີກການເລືອກ" : "ເລືອກທັງໝົດໃນໜ້ານີ້"}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={selectedIds.length === 0}
                onClick={() => setBulkVoidOpen(true)}
              >
                ຍົກເລີກ ({selectedIds.length})
              </Button>
            </div>
          </div>
        ) : null}

        <CreditInvoicesTable
          isLoading={isLoading}
          data={items}
          offset={query.offset}
          limit={query.limit}
          totalCount={total}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPaginationChange={(offset, limit) =>
            updateSearch({ offset, limit })
          }
          onSortingChange={(field, desc) =>
            updateSearch({
              sort: desc ? [`-${field}`] : [field],
              offset: 0,
            })
          }
          onView={(id) =>
            nav({ to: "/app/billing/credit-invoices/$id", params: { id } })
          }
          canVoid={!!canVoid}
          onVoid={(id) => setVoidInvoiceId(id)}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />

        {voidInvoiceId ? (
          <VoidCreditInvoiceDialog
            invoiceId={voidInvoiceId}
            open={voidInvoiceId != null}
            onOpenChange={(open) => {
              if (!open) setVoidInvoiceId(null);
            }}
            onSuccess={() => toast.success("ຍົກເລີກໃບສຳເລັດ")}
          />
        ) : null}

        <BulkVoidCreditInvoicesDialog
          invoiceIds={selectedIds}
          open={bulkVoidOpen}
          onOpenChange={setBulkVoidOpen}
          onSuccess={({ succeededCount, failedCount }) => {
            setSelectedIds([]);
            if (failedCount === 0) {
              toast.success(`ຍົກເລີກສຳເລັດ ${succeededCount} ໃບ`);
            } else {
              toast.warning(
                `ສຳເລັດ ${succeededCount} · ລົ້ມເຫຼວ ${failedCount}`,
              );
            }
          }}
        />
      </Main>
    </>
  );
}
